/**
 * @fileoverview AdminNavbar Component
 *
 * Main navigation component for RSH Portal admin interface.
 * Layout:
 * - Desktop: Logo (left) | Nav Links center | Tasks tab (hanging below) | Profile dropdown (right)
 * - Mobile: Logo + Profile (top bar) + Tasks tab (hanging below) | 5-item bottom nav
 *
 * The Tasks tab is a persistent button that hangs below the navbar on both
 * desktop and mobile. It toggles the tasks dropdown panel (owned by AdminPortal).
 * Previously this was dashboard-only on mobile and hidden on desktop.
 *
 * @component
 * @version 3.0.0
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";
import * as Sentry from "@sentry/react";
import {
  FaHome,
  FaProjectDiagram,
  FaUsers,
  FaHardHat,
  FaCalendarAlt,
  FaCloudUploadAlt,
  FaUserCircle,
  FaSlidersH,
  FaTools,
  FaSignOutAlt,
  // Weather widget icons — kept for future implementation
  // FaCloud,
  // FaSun,
  // FaCloudRain,
  // FaSnowflake,
  // FaCloudSun,
  // FaSmog,
  // FaBolt,
  FaChevronDown,
  FaUser,
  FaTasks,
  FaVial,
} from "react-icons/fa";
import useUserPreferences from "../../hooks/useUserPreferences";
import "./AdminNavbar.css";

/* ============================================
   WEATHER WIDGET — commented out, kept for future implementation
   ============================================ */

/*
const getWeatherIcon = (condition) => {
  const iconMap = {
    Clear: <FaSun className="admin-nav-weather-icon sunny" />,
    Clouds: <FaCloudSun className="admin-nav-weather-icon cloudy" />,
    Rain: <FaCloudRain className="admin-nav-weather-icon rainy" />,
    Drizzle: <FaCloudRain className="admin-nav-weather-icon rainy" />,
    Thunderstorm: <FaBolt className="admin-nav-weather-icon stormy" />,
    Snow: <FaSnowflake className="admin-nav-weather-icon snowy" />,
    Mist: <FaSmog className="admin-nav-weather-icon foggy" />,
    Fog: <FaSmog className="admin-nav-weather-icon foggy" />,
    Haze: <FaSmog className="admin-nav-weather-icon foggy" />,
  };
  return iconMap[condition] || <FaCloud className="admin-nav-weather-icon" />;
};

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Placeholder data — will be replaced with actual API call
        setWeather({
          temp: 52,
          condition: "Clouds",
          description: "Partly Cloudy",
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching weather:", error);
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="admin-nav-weather-badge">
        <FaCloud className="admin-nav-weather-icon loading" />
        <span className="admin-nav-weather-temp">--°</span>
      </div>
    );
  }

  if (!weather) return null;

  return (
    <div
      className="admin-nav-weather-badge"
      data-tooltip-bottom={`Gainesville, GA - ${weather.description}`}
    >
      {getWeatherIcon(weather.condition)}
      <span className="admin-nav-weather-temp">
        {Math.round(weather.temp)}°F
      </span>
    </div>
  );
};
*/

/* ============================================
   PROFILE DROPDOWN
   ============================================ */

/**
 * Profile Dropdown Menu Component
 * Contains navigation links (Uploads, Profile, Settings, Tools)
 * with Sign Out as the final separated item.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether dropdown is visible
 * @param {Function} props.onClose - Handler to close dropdown
 * @param {Function} props.onSignOut - Handler for sign out action
 * @returns {JSX.Element|null} Dropdown menu or null when closed
 */
const ProfileDropdown = ({
  isOpen,
  onClose,
  onSignOut,
  isITUser,
  showTestAccounts,
  onToggleTestAccounts,
}) => {
  const location = useLocation();

  /**
   * Checks if the given path matches the current route
   * @param {string} path - Route path to check
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  if (!isOpen) return null;

  /** Navigation items that appear above the sign-out divider */
  const menuItems = [
    {
      path: "/admin/profile",
      icon: <FaUser />,
      label: "My Profile",
    },
    {
      path: "/admin/calendar",
      icon: <FaCalendarAlt />,
      label: "Calendar",
    },
    {
      path: "/admin/settings",
      icon: <FaSlidersH />,
      label: "Settings",
    },
    {
      path: "/admin/tools",
      icon: <FaTools />,
      label: "Admin Tools",
    },
  ];

  return (
    <div className="admin-nav-profile-dropdown">
      {isITUser && (
        <>
          <label className="admin-nav-dropdown-toggle">
            <span className="admin-nav-dropdown-toggle-left">
              <span className="admin-nav-dropdown-icon">
                <FaVial />
              </span>
              <span className="admin-nav-dropdown-label">Test Mode</span>
            </span>
            <div className="admin-nav-toggle-switch">
              <input
                type="checkbox"
                checked={showTestAccounts}
                onChange={(e) => onToggleTestAccounts(e.target.checked)}
              />
              <span className="admin-nav-toggle-slider"></span>
            </div>
          </label>
          <div className="admin-nav-dropdown-divider" />
        </>
      )}
      {menuItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`admin-nav-dropdown-item ${
            isActive(item.path) ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span className="admin-nav-dropdown-icon">{item.icon}</span>
          <span className="admin-nav-dropdown-label">{item.label}</span>
        </Link>
      ))}
      {/* Divider */}
      <div className="admin-nav-dropdown-divider" />
      {/* Sign Out — always last */}
      <button
        className="admin-nav-dropdown-item admin-nav-dropdown-signout"
        onClick={() => {
          onClose();
          onSignOut();
        }}
      >
        <span className="admin-nav-dropdown-icon">
          <FaSignOutAlt />
        </span>
        <span className="admin-nav-dropdown-label">Sign Out</span>
      </button>
    </div>
  );
};

/* ============================================
   MAIN ADMIN NAVBAR
   ============================================ */

/**
 * Main AdminNavbar Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.userData - Current user data from Team table
 * @param {boolean} props.tasksPanelOpen - Whether the tasks dropdown is open
 * @param {Function} props.onToggleTasksPanel - Toggle handler for tasks panel
 * @param {number} props.tasksBadgeCount - Number of tasks for today (shown as badge)
 * @returns {JSX.Element} AdminNavbar with desktop and mobile layouts
 */
function AdminNavbar({
  userData,
  tasksPanelOpen,
  onToggleTasksPanel,
  tasksBadgeCount,
  isITUser,
  showTestAccounts,
  onToggleTestAccounts,
  tasksDropdownContent,
}) {
  const [user] = useAuthState(auth);
  const { preferences } = useUserPreferences();
  const avatarUrl = preferences?.avatarUrl || null;
  const navigate = useNavigate();
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDesktopRef = useRef(null);
  const profileMobileRef = useRef(null);

  // Close dropdown when clicking outside either profile container
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideDesktop = profileDesktopRef.current?.contains(event.target);
      const isInsideMobile = profileMobileRef.current?.contains(event.target);

      if (!isInsideDesktop && !isInsideMobile) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  /**
   * Handle user sign out with Sentry cleanup
   */
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      Sentry.setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      Sentry.captureException(error);
    }
  };

  /**
   * Check if the given path matches the current route
   * @param {string} path - Route path to check
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return location.pathname === path;
  };

  /**
   * Toggle the profile dropdown open/closed
   */
  const toggleProfileDropdown = () => {
    setProfileDropdownOpen((prev) => !prev);
  };

  /** Display name for the profile area */
  const displayName =
    userData?.name ||
    user?.displayName ||
    user?.email?.split("@")[0] ||
    "Admin";

  /** Role/title for the profile area */
  const displayRole =
    userData?.role || userData?.titlePosition?.[0] || "Team Member";

  // Desktop nav links — includes Dashboard as a regular link
  const desktopNavLinks = [
    {
      path: "/admin/dashboard",
      icon: <FaHome />,
      label: "Dashboard",
    },
    {
      path: "/admin/projects",
      icon: <FaProjectDiagram />,
      label: "Projects",
    },
    {
      path: "/admin/clients",
      icon: <FaUsers />,
      label: "Clients",
    },
    {
      path: "/admin/vendors",
      icon: <FaHardHat />,
      label: "Vendors",
    },
    {
      path: "/admin/uploads",
      icon: <FaCloudUploadAlt />,
      label: "Upload",
    },
  ];

  // Mobile bottom nav — Dashboard stays as raised center
  const mobileNavLinks = [
    {
      path: "/admin/dashboard",
      icon: <FaHome />,
      label: "Dashboard",
    },
    {
      path: "/admin/projects",
      icon: <FaProjectDiagram />,
      label: "Projects",
    },
    {
      path: "/admin/clients",
      icon: <FaUsers />,
      label: "Clients",
    },

    {
      path: "/admin/vendors",
      icon: <FaHardHat />,
      label: "Vendors",
    },
    {
      path: "/admin/uploads",
      icon: <FaCloudUploadAlt />,
      label: "Upload",
    },
  ];

  return (
    <div className="admin-nav-wrapper">
      {/* ==================== DESKTOP TOP BAR ==================== */}
      <nav className="admin-nav-desktop">
        <div className="admin-nav-desktop-container">
          {/* Logo — left */}
          <Link to="/admin/dashboard" className="admin-nav-logo">
            <img
              src="/rsh-logo-sq.png"
              alt="Red Setter Homes"
              className="admin-nav-logo-image"
            />
            <div className="admin-nav-logo-text">
              <p className="admin-nav-logo-title">Red Setter Homes</p>
              <p className="admin-nav-logo-subtitle">Management Portal</p>
            </div>
          </Link>

          {/* Navigation Links — center */}
          <div className="admin-nav-desktop-links">
            {desktopNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`admin-nav-desktop-link ${
                  isActive(link.path) ? "active" : ""
                }`}
              >
                <span className="admin-nav-link-icon">{link.icon}</span>
                <span className="admin-nav-link-label">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Right Side — Weather + Profile */}
          <div className="admin-nav-desktop-right">
            {/*<WeatherWidget />*/}

            {/* Profile trigger + dropdown */}
            <div
              className="admin-nav-profile-container"
              ref={profileDesktopRef}
            >
              <button
                className={`admin-nav-profile-trigger ${
                  profileDropdownOpen ? "open" : ""
                }`}
                onClick={toggleProfileDropdown}
                aria-label="Profile menu"
              >
                {showTestAccounts ? (
                  <div className="admin-nav-mobile-profile-icon">
                    <FaVial className="admin-test-mode" />
                  </div>
                ) : avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="admin-nav-profile-avatar"
                  />
                ) : (
                  <FaUserCircle className="admin-nav-mobile-profile-icon" />
                )}
                <div className="admin-nav-profile-info">
                  <span className="admin-nav-profile-name">{displayName}</span>
                  <span className="admin-nav-profile-role">{displayRole}</span>
                </div>
                <FaChevronDown
                  className={`admin-nav-profile-chevron ${
                    profileDropdownOpen ? "rotated" : ""
                  }`}
                />
              </button>
              <ProfileDropdown
                isOpen={profileDropdownOpen}
                onClose={() => setProfileDropdownOpen(false)}
                onSignOut={handleSignOut}
                isITUser={isITUser}
                showTestAccounts={showTestAccounts}
                onToggleTestAccounts={onToggleTestAccounts}
              />
            </div>
          </div>
        </div>

        {/* Desktop Tasks tab — hangs below navbar, visible on all pages */}
        <button
          className={`admin-nav-tasks-tab admin-nav-tasks-tab-desktop ${
            tasksPanelOpen ? "active" : ""
          }`}
          onClick={onToggleTasksPanel}
          aria-label="Toggle tasks panel"
        >
          <FaTasks />
          <span className="admin-nav-tasks-tab-label">All Project Tasks</span>
          {tasksBadgeCount > 0 && (
            <span className="admin-nav-tasks-badge">{tasksBadgeCount}</span>
          )}
        </button>
      </nav>

      {/* ==================== MOBILE TOP BAR ==================== */}
      <div className="admin-nav-mobile-top">
        <Link to="/admin/dashboard" className="admin-nav-mobile-logo-link">
          <img
            src="/rsh-logo-sq.png"
            alt="RSH"
            className="admin-nav-mobile-logo"
          />
          <div className="admin-nav-mobile-logo-text">
            <p className="admin-nav-mobile-title">Red Setter Homes</p>
            <p className="admin-nav-mobile-subtitle">Management Portal</p>
          </div>
        </Link>

        <div className="admin-nav-mobile-top-right">
          {/*<WeatherWidget />*/}

          {/* Mobile profile trigger + dropdown */}
          <div className="admin-nav-profile-container" ref={profileMobileRef}>
            <button
              className={`admin-nav-mobile-profile-btn ${
                profileDropdownOpen ? "open" : ""
              }`}
              onClick={toggleProfileDropdown}
              aria-label="Profile menu"
            >
              {showTestAccounts ? (
                <div className="admin-nav-mobile-profile-icon">
                  <FaVial className="admin-test-mode-mobile" />
                </div>
              ) : avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="admin-nav-profile-avatar admin-nav-profile-avatar-mobile"
                />
              ) : (
                <FaUserCircle className="admin-nav-mobile-profile-icon" />
              )}
            </button>
            <ProfileDropdown
              isOpen={profileDropdownOpen}
              onClose={() => setProfileDropdownOpen(false)}
              onSignOut={handleSignOut}
              isITUser={isITUser}
              showTestAccounts={showTestAccounts}
              onToggleTestAccounts={onToggleTestAccounts}
            />
          </div>
        </div>

        {/* Mobile Tasks tab — hangs below navbar, visible on all pages */}
        <button
          className={`admin-nav-tasks-tab admin-nav-tasks-tab-mobile ${
            tasksPanelOpen ? "active" : ""
          }`}
          onClick={onToggleTasksPanel}
          aria-label="Toggle tasks panel"
        >
          <FaTasks />
          <span className="admin-nav-tasks-tab-label">Tasks</span>
          {tasksBadgeCount > 0 && (
            <span className="admin-nav-tasks-badge">{tasksBadgeCount}</span>
          )}
        </button>
      </div>

      {/* ==================== MOBILE BOTTOM NAV ==================== */}
      <nav className="admin-nav-mobile-bottom">
        {mobileNavLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`admin-nav-item ${isActive(link.path) ? "active" : ""}`}
          >
            <span className="admin-nav-icon">{link.icon}</span>
            <span className="admin-nav-label">{link.label}</span>
          </Link>
        ))}
      </nav>

      {/* Tasks dropdown — rendered here to share stacking context with profile dropdown */}
      {tasksDropdownContent}
    </div>
  );
}

export default AdminNavbar;
